import _, { concat, differenceBy, endsWith, filter, find, first, forEach, map, size, toNumber, orderBy as _orderBy, isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { DeepPartial } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Navbar, NavbarBrand, NavbarText, Table, UncontrolledDropdown } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { AddUserForm } from '.';
import { Loading } from '..';
import { Group, HacpackMutation, HacpackMutationAddOrChangeMembershipArguments, HacpackQuery, Role, User } from '../../types/graph';
import { useCurrentUser } from '../../hooks';
import styled from '../../themes/themed-styled-components';
import { RoleName } from '../../types';
import { GridLayout } from '../ui';
import { Key } from 'react';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';

const VIRTUAL_DOMAIN = process.env.REACT_APP_VIRTUAL_USER_DOMAIN;

const groupsQuery = gql<HacpackQuery, {}>`query {
    groups {
        id
        name
        roles {
            id
            name
            users {
                id
                login
                email
                managerId
                displayName
            }
        }
    }
}`;

const addOrChangeMembershipMutation = gql<HacpackMutation, HacpackMutationAddOrChangeMembershipArguments>`mutation ($groupId: Int!, $roleIds: [Int!]!, $userId: Int, $email: String, $message: String, $managerId: Int, $forceEmail: Boolean) {
    addOrChangeMembership(groupId: $groupId, roleIds: $roleIds, userId: $userId, email: $email, message: $message, managerId: $managerId, forceEmail: $forceEmail)  {
        isOk
        data {
            id
            login
            email
            displayName
            roles {
                id
            }
        }
    }
}`;

//function rolesBoxReducer(state: { roles: Role[] }, action: { type: 'add' | 'remove', payload: Role } | { type: 'set', payload: Role[] }) {
//    switch (action.type) {
//        case 'set':
//            return { roles: action.payload };
//        case 'add':
//            const addRole: Role = action.payload;
//            return { roles: concat(state.roles, addRole) };
//        case 'remove':
//            const removeRole: Role = action.payload;
//            return {
//                roles: filter(state.roles, (role) => role.id !== removeRole.id)
//            }
//        default:
//            throw new Error();
//    }
//}

const RoleButton = styled(Button)`
    :not(:disabled):not(.disabled):hover {
        text-decoration: line-through;
    }
`;

type RolesBoxProps = {
    value?: DeepPartial<Role>[],
    disabled?: boolean,
    options?: DeepPartial<Role>[],
    min?: number,
    max?: number,
    onValueChange?: (value: DeepPartial<Role>[]) => void
}

type RequestPasswordResetMutationResponse = {
    requestPasswordReset: {
        isOk?: boolean
    }
};

type RequestPasswordResetMutationVariables = {
    loginOrEmail?: string
};

const createResetPasswordTokenMutation = gql<RequestPasswordResetMutationResponse, RequestPasswordResetMutationVariables>`
    mutation ($loginOrEmail: String){
        requestPasswordReset (loginOrEmail: $loginOrEmail) {
            isOk
        }
    }
`;

const RolesBox = ({ value, options, disabled, min, max, onValueChange }: RolesBoxProps) => {
    const { t } = useTranslation('common');
    const roles = useMemo(() => value ?? [], [value]);

    const addableOptions = useMemo(() => differenceBy(options, roles, 'id'), [options, roles]);

    const canRemove = useMemo(() => !disabled && size(roles) > (min ?? 0), [disabled, roles, min]);
    const canAdd = useMemo(() => !disabled && size(addableOptions) > 0 && size(roles) < (max ?? Infinity), [disabled, roles, addableOptions, max]);

    const addRole = useCallback((role: Role) => onValueChange?.(concat(roles, role)), [roles, onValueChange]);
    const removeRole = useCallback((role: Role) => onValueChange?.(filter(roles, (r) => r.id !== role.id)), [roles, onValueChange]);

    const addDropdown = useMemo(() => canAdd
        ? <UncontrolledDropdown>
            <DropdownToggle color="secondary">+</DropdownToggle>
            <DropdownMenu right>
                {map(addableOptions, (role: any) =>
                    <DropdownItem key={role.id} onClick={() => addRole(role)}>
                        {t(`userManagement.role.${role.name}.name`)}
                    </DropdownItem>
                )}
            </DropdownMenu>
        </UncontrolledDropdown>
        : null, [addableOptions, canAdd, addRole, t]);

    return <div className="d-flex justify-content- align-items-center">
        <div className="d-flex justify-content-left align-items-center">
            {map(roles, (role: any) =>
                <RoleButton disabled={!canRemove} key={role.id} color="text" onClick={() => removeRole(role)}>
                    {t(`userManagement.role.${role.name}.name`)}
                </RoleButton>
            )}
        </div>
        {addDropdown}
    </div>;
}

type TableRowProps = {
    user: DeepPartial<User>,
    group: DeepPartial<Group> | null,
    onSave?: (user: DeepPartial<User>) => void
}

//function TableRowReducer(state: { user: User }, action: { type: 'set', payload: User }) {
//    switch (action.type) {
//        case 'set':
//            return { user: action.payload };
//        default:
//            throw new Error();
//    }
//}

const TableRow = ({ user: defaultUser, group, onSave }: TableRowProps) => {
    //const { t } = useTranslation('common');
    const { isSuperuser/*, hasRole*/, currentUser, hasRole } = useCurrentUser();


    const [createResetPasswordTokenResult, createResetPasswordToken] = useMutation(createResetPasswordTokenMutation);
    const { fetching: resetPasswordFetching } = createResetPasswordTokenResult;

    //const isAdmin = useMemo(() => hasRole(RoleName.ADMIN), [hasRole]);

    const [user, setUser] = useState<DeepPartial<User>>(defaultUser);
    const groupRoles = group?.roles ?? null;
    const [editMode, setEditMode] = useState<boolean>(false);

    const onRolesChange = useCallback((roles: any) => setUser(({ ...userRest }) => ({ ...userRest, roles })), [setUser]);
    const toggleEditMode = useCallback(() => setEditMode(editMode => !editMode), [setEditMode]);
    const onSaveButtonClick = useCallback(() => onSave?.(user), [onSave, user]);
    const onUndoButtonClick = useCallback(() => {
        setUser(defaultUser);
        toggleEditMode();
    }, [setUser, defaultUser, toggleEditMode]);

    useEffect(() => {
        setUser(defaultUser);
        setEditMode(false);
    }, [setEditMode, setUser, defaultUser]);

    const { id, login, email, displayName, roles, managerId } = user;

    const managedUser = isSuperuser || groupRoles?.find(r => r?.name === RoleName.ADMIN);

    const assignableRoles = endsWith(email, `@${VIRTUAL_DOMAIN}`) ? filter(groupRoles, (r: any) => r.name !== RoleName.ADMIN) : groupRoles ?? [];

    const resetUserPassword = useCallback(async () => {
        const username = displayName ?? login;
        const result = await createResetPasswordToken({ loginOrEmail: email });

        if (result.data?.requestPasswordReset?.isOk === true) {
            window.alert(isNumber(managerId)
                ? `Una email con il link di per reimpostare la password � stata inviata al gestore dell'utente ${username}.`
                : `Una email con il link di per reimpostare la password � stata inviata all'utente ${username}.`);

            return;
        }

        window.alert("Si � verificato un errore.");

    }, [createResetPasswordToken, email, login, displayName, managerId]);

    return <tr key={id}>
        {isSuperuser && <th scope="row">{id}</th>}
        <td>{login}</td>
        <td>{email}</td>
        <td>{displayName}</td>
        <td><RolesBox value={filter(roles, (r: Role) => r.group?.id === group?.id) as DeepPartial<Role>[]} disabled={!editMode} options={assignableRoles as DeepPartial<Role>[]} min={0} onValueChange={onRolesChange} /></td>
        <td>
            <div className="d-flex justify-content-end align-items-center gap-1">
                {managedUser && <Button onClick={() => resetUserPassword()} disabled={resetPasswordFetching}>Reimposta password</Button>}
                {editMode
                    ? <>
                        <Button onClick={onSaveButtonClick} color="primary">Salva</Button>
                        <Button className="ml-1" onClick={onUndoButtonClick} color="secondary">Annulla</Button>
                    </>
                    : <Button onClick={toggleEditMode} color="secondary">Modifica</Button>}
            </div>
        </td>
    </tr>
}

const StyledTable = styled(Table)`
    & th, & td {
        vertical-align: middle;
    }
`;


const UserManagement = () => {

    const { /*currentUser, fetchingCurrentUser,*/ isSuperuser } = useCurrentUser();

    const [addMode, setAddMode] = useState(false);

    const [groupsResult/*, reexecuteGroupsQuery*/] = useQuery({
        query: groupsQuery
    });

    const { data/*, fetching: fetchingGroups*/ } = groupsResult;

    const { 1: addOrChangeMembership } = useMutation(addOrChangeMembershipMutation);

    //const { } = addOrChangeMembershipResult;

    const [selectedGroup, setSelectedGroup] = useState<DeepPartial<Group> | null>(null);

    const { t } = useTranslation('common');

    const [orderBy, setOrderBy] = useState<[keyof User, 'asc' | 'desc'] | null>(null);

    const [/*users, roles,*/ groups, memberships] = useMemo(() => {
        const users: DeepPartial<User>[] = [];
        const groups: DeepPartial<Group>[] = [];
        const roles: DeepPartial<Role>[] = [];

        forEach(data?.groups, ({ roles: groupRoles, ...groupRest }) => {
            const group: DeepPartial<Group> = { ...groupRest };

            group.roles = map(groupRoles, ({ ...roleRest }) => {
                const role: DeepPartial<Role> = { ...roleRest, group };
                roles.push(role);
                forEach(role.users, ({ id, ...userRest }: any) => {
                    const user = find(users, ['id', id]);
                    if (user?.roles !== undefined) {
                        user.roles?.push(role);
                        return;
                    }
                    users.push({ id, ...userRest, roles: [role], memberships: [] });
                });

                return role;
            });

            groups.push(group);
        });

        const memberships = _(users).map((user) => {
            user.memberships = _(user.roles).map('group').uniqBy('id').map((group) => ({
                user, group, roles: filter(user.roles, ['group.id', group?.id])
            })).value();

            return user.memberships;
        }).flatten().value();

        return [/*users, roles,*/ groups, memberships];
    }, [data]);

    useEffect(() => setSelectedGroup((selectedGroup) => {
        const group = first(groups);
        if (group === undefined) {
            return null;
        }

        if (selectedGroup === null) {
            return group;
        }

        return selectedGroup;
    }), [groups, setSelectedGroup]);

    const groupSelector = useMemo(() => (addMode || size(groups) < 2) ? <NavbarText>{selectedGroup?.name}</NavbarText> : <FormGroup>
        {/*<Label for="group" className="mr-sm-2">Gruppo</Label>*/}
        <Input id="group" type="select" value={selectedGroup?.id} onChange={(ev) => setSelectedGroup(find(groups, ['id', toNumber(ev.target.value)]) ?? null)}>
            {map(groups, ({ id, name }) => <option key={id} value={id}>{name}</option>)}
        </ Input>
    </FormGroup>, [selectedGroup, setSelectedGroup, groups, addMode]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    //console.log(rows);
    const header = useMemo(() => <div ref={containerRef}><Navbar className="navbar ng-white border-bottom box-shadow" light>
        <NavbarBrand>{t('userManagement.title')}</NavbarBrand>
        <div className="d-sm-inline-flex flex-sm-row">
            <Form tag="div" inline>
                {groupSelector}
            </Form>
        </div>
        <div className="navbar-right d-sm-inline-flex flex-sm-row">
            {addMode
                ? null : <Button color="secondary" className="ml-sm-2 form-control" onClick={() => setAddMode(true)}>Aggiungi utente</Button>
            }
        </div>
    </Navbar></div>, [groupSelector, addMode, setAddMode, t])

    const visibleUsers = useMemo<DeepPartial<User>[]>(() => _(memberships).filter(['group.id', selectedGroup?.id]).map((m: any) => m.user as User).value(), [selectedGroup?.id, memberships/*, t*/]);

    const handleRowSave = useCallback((user: DeepPartial<User>) =>
        addOrChangeMembership({
            groupId: selectedGroup?.id as number,
            roleIds: map(user?.roles, 'id') as number[],
            userId: user.id as number,
            email: null,
            message: null
        }), [selectedGroup, addOrChangeMembership]);


    const handleAddUserSubmit = useCallback(({ roleIds, userId, email, message, managerId, forceEmail }: any) =>
        addOrChangeMembership({
            groupId: selectedGroup?.id as number,
            roleIds,
            userId,
            email,
            message,
            forceEmail,
            managerId
        }).then((value) => {
            if (value?.error !== undefined) {
                console.error(value?.error);
                return;
            }

            setAddMode(false);
        }), [selectedGroup, addOrChangeMembership, setAddMode]);

    const makeHeader = useCallback((field: keyof User) => {
        const [sortColumn, sortDirection] = orderBy ?? [null, 'asc'];

        const current = sortColumn === field;

        const direction = current && sortDirection === 'asc' ? 'desc' : 'asc';

        return <th>
            <button className="btn btn-link" onClick={() => setOrderBy([field, direction])}>
                {t(`userManagement.columns.${field}.name`.toString())}
                {current && (sortDirection === 'desc' ? <FaArrowDown /> : <FaArrowUp />)}
            </button>

        </th>;
    }, [orderBy, setOrderBy, t]);

    const tableHeader = useMemo(() => <thead>
        <tr>
            {isSuperuser && makeHeader('id')}
            {makeHeader('login')}
            {makeHeader('email')}
            {makeHeader('displayName')}
            <th>{t('userManagement.columns.roles.name')}</th>
            <th><span className="sr-only"></span></th>
        </tr>
    </thead>, [t, isSuperuser, makeHeader]);

    const orderedUsers = useMemo(() => {
        if (orderBy === null) {
            return visibleUsers;
        }

        const [sortColumn, sortDirection] = orderBy;

        return _orderBy(visibleUsers, sortColumn, sortDirection);
    }, [orderBy, visibleUsers])

    const table = useMemo(() => <StyledTable striped>
        {tableHeader}
        <tbody>
            {map(orderedUsers, (user) => <TableRow key={user.id} user={user} group={selectedGroup} onSave={handleRowSave} />)}
        </tbody>
    </StyledTable>, [orderedUsers, isSuperuser, handleRowSave, selectedGroup, tableHeader]);

    const addUserForm = useMemo(() => selectedGroup === null ? null : <AddUserForm group={selectedGroup} onSubmit={handleAddUserSubmit} onUndo={() => setAddMode(false)} />, [selectedGroup, handleAddUserSubmit, setAddMode]);

    return <GridLayout header={header}>
        <Helmet>
            <title>{t('userManagement.title')} :: {t('app.title')}</title>
        </Helmet>
        {data === undefined ? <Loading /> : addMode ? addUserForm : table}
    </GridLayout>;
}

export default UserManagement;